/**
 * RCM library (Phase 4 — v2 parity).
 *
 * Sortable Cloudscape Table with text filter. Clicking any row opens a
 * read-only modal with the full control detail: description + risk as
 * textareas, owner / frequency / control_type in a 2-column form, plus a
 * Pass/Fail status indicator for last_audit_result.
 *
 * Sourced from /rcm-controls (mounted by sections/cuecs/router.py — RCM data
 * is cross-section so it sits in shell/).
 */
import { useCallback, useEffect, useState } from "react";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  Alert,
  Box,
  Button,
  Header,
  Spinner,
  Table,
  TextFilter,
} from "@cloudscape-design/components";
import type { RcmControl } from "@/core/api/types";
import { RcmDetailModal } from "@/core/ui/RcmDetailModal";
import { listRcmControls } from "@/sections/cuecs/api";

export function RcmLibrary() {
  const [rows, setRows] = useState<RcmControl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<RcmControl | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listRcmControls());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const { items, collectionProps, filterProps, filteredItemsCount } = useCollection(rows, {
    filtering: {
      filteringFunction: (item, text) => {
        const q = (text ?? "").toLowerCase();
        if (!q) return true;
        return [
          item.code,
          item.name,
          item.description,
          item.process,
          item.owner,
          item.frequency,
          item.control_type,
          item.risk,
          item.system,
        ]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      },
    },
    sorting: { defaultState: { sortingColumn: { sortingField: "code" }, isDescending: false } },
  });

  if (loading && !rows.length) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner /> Loading RCM library…
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="l">
        <Alert type="error" header="Failed to load RCM" action={<Button onClick={refresh}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Table
        {...collectionProps}
        items={items}
        onRowClick={({ detail: d }) => setDetail(d.item)}
        variant="container"
        header={
          <Header
            variant="h1"
            counter={`(${rows.length})`}
            actions={<Button onClick={refresh} iconName="refresh" disabled={loading} />}
          >
            RCM library
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder="Find by code, description, process, owner, risk…"
            countText={`${filteredItemsCount} match${filteredItemsCount === 1 ? "" : "es"}`}
          />
        }
        columnDefinitions={[
          {
            id: "code",
            header: "Control ID",
            sortingField: "code",
            cell: (it) => <Box variant="strong">{it.code}</Box>,
            width: 140,
          },
          {
            id: "description",
            header: "Description",
            sortingField: "description",
            cell: (it) => (
              <Box variant="small" color="text-body-secondary">
                <span
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {it.description ?? "—"}
                </span>
              </Box>
            ),
            maxWidth: 460,
          },
          {
            id: "process",
            header: "Process",
            sortingField: "process",
            cell: (it) => it.process ?? "—",
            width: 200,
          },
          {
            id: "system",
            header: "System",
            sortingField: "system",
            cell: (it) => it.system ?? "—",
            width: 150,
          },
          {
            id: "risk",
            header: "Risk",
            sortingField: "risk",
            cell: (it) => (
              <Box variant="small" color="text-body-secondary">
                {it.risk ?? "—"}
              </Box>
            ),
            width: 280,
          },
        ]}
        empty={
          <Box textAlign="center" color="text-body-secondary" padding="m">
            No RCM controls found for this company.
          </Box>
        }
      />

      <RcmDetailModal
        control={detail}
        onDismiss={() => setDetail(null)}
        onSaved={() => void refresh()}
      />
    </>
  );
}
